import {
  UiOptionHelper,
  UiOptionTypes,
} from '@foam/buoy/src/components/combobox/types';
import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { Creator } from '~/clientApi/creator.model';
import { ALL_TALENT_ROSTER_QUERY_KEY } from '~/queries/keys';
import {
  Filter,
  NAME_OR_HANDLE_SEARCH_BY_FIELDS,
  SearchCreatorsApiResponse,
  allowedSortDirections,
  allowedSortFields,
  searchCreatorApi,
} from '~/searchApi';
import { mapCreatorFromSearchApiModelToFoamApiModel } from '~/searchApi/searchApi.mapper';

type CreatorSearchQueryKey = [
  typeof ALL_TALENT_ROSTER_QUERY_KEY,
  {
    filters: Filter[];
    sortField: allowedSortFields;
    sortDirection: allowedSortDirections;
    managerId: string;
    searchTerm: string;
  },
];

/**
 * Max page size is 10k on backend. A future version of Autocompletewill ensure virtualisation above a reasonable limit.
 * A further version might even implement 'fetch-next-page-on-bottom-scroll' in the Autocomplete dropdown.
 */
const PAGE_SIZE = 10000;

export const useCreatorAutocompleteQuery = (
  queryOptions: UseQueryOptions<
    SearchCreatorsApiResponse,
    unknown,
    UiOptionHelper<{ creator: Creator }>[],
    CreatorSearchQueryKey
  >,
): UseQueryResult<UiOptionHelper<{ creator: Creator }>[], unknown> => {
  return useQuery({
    ...queryOptions,
    queryFn: ({ queryKey }) => {
      const { searchTerm, sortField, sortDirection, filters } = queryKey[1];
      const invalidCharacters = ['*', ' ', '"'];
      const isQueryValid = !invalidCharacters.includes(
        searchTerm.charAt(searchTerm.length - 1),
      );

      return searchCreatorApi.getCreators({
        filters: filters,
        page: 1,
        size: PAGE_SIZE,
        order_by: sortField,
        order: sortDirection,
        q: `${searchTerm}${isQueryValid ? '*' : ''}`,
        q_fields: NAME_OR_HANDLE_SEARCH_BY_FIELDS,
      });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes stale time
    retry: 1,
    select: (data) => {
      // this function is used to map the data from the search api to the view model and make them compatible with the current components
      // also we pass the manager id to the mapper to check the co-management relation to each creator and fill the property in the mapper
      return data?.results?.map((item) => {
        const creator = mapCreatorFromSearchApiModelToFoamApiModel({
          creator: item,
          caller: { id: queryOptions.queryKey?.[1].managerId ?? '' },
        });
        return {
          uniqueKey: getDefinitelyUniqueIdFromCreator(creator),
          label: creator.fullName,
          value: creator.id,
          creator: creator,
          optionType: UiOptionTypes.item,
        };
      });
    },
  });
};

/**
 * Keyboard accessibility in Shadcn command breaks if CommandItems have duplicate values.
 * To avoid this, we go overboard on generating unique ids for each creator.
 */
export function getDefinitelyUniqueIdFromCreator(creator: Creator) {
  return [
    creator.slug,
    creator.id,
    creator.fullName,
    creator.agency?.id,
    creator.agency?.name,
    creator.updatedAt,
  ]
    .filter(Boolean)
    .join('-');
}
